import {Component} from 'panel';

import {registerMPElement} from 'common/util/register-element';

import {CHANGE_TYPE_SENT_FEEDBACK} from '../constants';
import {Alert} from '../models.js';
import {fetchAlert} from '../utils/api';
import {getSentFeedbackAtMs, setSentFeedbackAtMs} from '../utils/common';
import {buildGlobalChangedAlert, buildGlobalChangeHandler} from '../utils/globalEvents.js';
import {trackAlertEvent} from '../utils/tracking';

import template from './index.jade';
import css from './index.styl?inline';

// observed events
// listens for globalChangedAlert to know when the feedback status has changed
export default class SmartHubAlertV2 extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      alert: `json`,
      'alert-color': `string`,
      'display-context': `string`,
      'explains-value': `json`,
      'project-id': `string`,
      'utc-offset': `string`,
      visible: `boolean`,
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        stopFeedbackAnimation: false,
      },
      appState: [`alert`, `projectId`],
      helpers: {
        generatePermalink: () => {
          return this.contextSpecificPermalink();
        },
        getShareLink: () => {
          return this.contextSpecificPermalink(true, true);
        },
        handleGoBack: (event) => {
          event.preventDefault();

          const navigateToLink = () => {
            const backHref = this.getAlert().generatePermalink();
            if (event.detail.modifierKey) {
              window.open(backHref);
            } else {
              window.location.href = backHref;
            }
          };

          trackAlertEvent(`Smart Hub: Clicked Back Button`, this.getAlert(), this.context).then(navigateToLink);

          // Navigate to link if the mixpanel track calls doesn't finish within 300ms.
          setTimeout(navigateToLink, 300);
        },
        handleShared: (event) => {
          event.preventDefault();

          const trackingParams = {};

          if (this.selectedExplains) {
            const {property, propertyValue} = this.selectedExplains;
            const explains = this.getAlert().findExplains(property, propertyValue);
            trackingParams[`explain_property`] = property;
            trackingParams[`explain_value`] = propertyValue;
            trackingParams[`explains_priority`] = explains.priority;
            trackingParams[`explains_type`] = explains.propertyType;
          }

          trackAlertEvent(`Smart Hub: Shared Link`, this.getAlert(), this.context, trackingParams);
        },
        handleSentFeedback: (event) => {
          event.preventDefault();
          const {isUseful, sentFeedbackAtMs} = event.detail;

          const params = {
            is_useful: isUseful,
            display_context: `IN_REPORT`,
          };
          this.fetch(`PATCH`, params);

          const trackingParams = {
            is_useful: isUseful,
          };

          if (this.selectedExplains) {
            const {property, propertyValue} = this.selectedExplains;
            const explains = this.getAlert().findExplains(property, propertyValue);
            trackingParams[`explain_property`] = property;
            trackingParams[`explain_value`] = propertyValue;
            trackingParams[`explains_priority`] = explains.priority;
            trackingParams[`explains_type`] = explains.propertyType;
          }

          trackAlertEvent(`Smart Hub: Sent Feedback`, this.getAlert(), this.context, trackingParams);

          this.setSentFeedbackAtMs(sentFeedbackAtMs);

          this.dispatchEvent(buildGlobalChangedAlert(CHANGE_TYPE_SENT_FEEDBACK, this.alert, {isUseful}));
        },
        hasSelectedExplains: () => !!this.selectedExplains,
        isAnomaly: () => !!this.getAlert().anomaly,
        isFeedbackDisabled: () => {
          return !!getSentFeedbackAtMs(this.appState.alert);
        },
        showFeedback: () => {
          return this.getAlert().isAlertOwner && !this.state.stopFeedbackAnimation;
        },
      },
    };
  }

  contextSpecificPermalink(absolute = false, shared = false) {
    if (this.selectedExplains) {
      const explain = this.getAlert().explains.find((ex) => {
        return (
          ex.property === this.selectedExplains.property && ex.propertyValue === this.selectedExplains.propertyValue
        );
      });

      if (explain) {
        return explain.generatePermalink(absolute, shared);
      } else {
        console.error(`expected to find a matching explain but could not, defaulting to alert link`);
      }
    }

    return this.getAlert().generatePermalink(absolute, shared);
  }

  // Only used in tests to inject spy dependency
  set appApi(value) {
    this._appApi = value;
  }

  get appApi() {
    return this._appApi;
  }

  get projectId() {
    return this.appState.projectId;
  }

  get alert() {
    return this.appState.alert;
  }

  get alertView() {
    return this.selectedExplains ? `Explain View` : `Card View`;
  }

  // In what context is this alert being viewed?
  get context() {
    return {
      alertView: this.alertView,
      displayContext: this.attr(`display-context`),
    };
  }

  get selectedExplains() {
    return this.attr(`explains-value`);
  }

  connectedCallback() {
    this.updateAlertState();

    super.connectedCallback(...arguments);

    if (!this.isPanelRoot) {
      throw new Error(`smart-hub-alert-v2 maintains its own state tree and should not be rendered as a child`);
    }

    this._handleGlobalChangedAlert = buildGlobalChangeHandler(this, this.alert);
    document.addEventListener(`globalChangedAlert`, this._handleGlobalChangedAlert);
  }

  disconnectedCallback() {
    super.disconnectedCallback(...arguments);
    document.removeEventListener(`globalChangedAlert`, this._handleGlobalChangedAlert);
  }

  updateAlertState() {
    this.updateApp({
      alert: this.attr(`alert`),
      projectId: this.attr(`project-id`),
    });
  }

  getAlert() {
    return Alert.create(this.alert, this.projectId, this.attr(`workspace-id`));
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);
    if (attr === `alert`) {
      this.updateAlertState();
    } else if (attr === `visible`) {
      this.stopFeedbackAnimationIfNeeded();
    }
  }

  fetch(method, params) {
    return fetchAlert(this.alert, this.projectId, method, params, this.appApi);
  }

  setSentFeedbackAtMs(ms) {
    const {alert} = this;
    setSentFeedbackAtMs(alert, ms);
    this.updateApp({alert});
  }

  stopFeedbackAnimationIfNeeded() {
    // unmount the feedback button component
    if (getSentFeedbackAtMs(this.alert)) {
      this.update({stopFeedbackAnimation: true});
    }
  }
}

registerMPElement(`mp-smart-hub-alert-v2`, SmartHubAlertV2);
